// The one shape every Briefing renders as.
//
// Whatever a briefing is ABOUT — a web search, an opened image, the email inbox — the design
// draws it the same way (frame 40006859:278034): a coloured title line naming the subject, then
// a short list of icon + fact rows. Normalising to that here is what lets one strip in the
// SmartBox render all of them, and what lets a new briefing be added by writing one builder
// rather than another block of template.
//
//   { kind, title, titleIcon, titleColor, subtitle,
//     rows: [{ key, icon, text, filter?, active?, action? }], packs, kindItems, selected, loading }
//
// `kind` is the briefing's identity — the store resolves which one is active by kind, and the
// strip keys its transition on it.

/** The colour the title line takes. The design uses the primary for every briefing so far. */
export const BRIEFING_PRIMARY = '#3741f3'

/**
 * A count as a briefing row writes it: "2,322", never "2322" (busy-my-web#603, Saffet 2026-10-09:
 * "if it is 2030 it should be like this 2,030").
 *
 * One helper rather than a `toLocaleString()` in every label, because the card used to carry both —
 * the total and today's went through it and the other eight did not, so "1,051 emails" sat next to
 * "2322 attachments". The locale is FIXED to en-GB on purpose: a bare `toLocaleString()` follows the
 * browser, so the same card read "2.322" in a German browser and the tests depended on the machine
 * that ran them. Anything that is not a number counts as zero, the rule every label here already had.
 */
export const COUNT_LOCALE = 'en-GB'
export function countText(n) {
  return (Number(n) || 0).toLocaleString(COUNT_LOCALE)
}

/**
 * EVERY Briefing row that starts with a count: a capital letter on the word after the number
 * (busy-my-web#609, Saffet 2026-10-09, Slack p1791531260546049: "Everything listed in a briefing
 * should have a capital letter after a Number").
 *
 * #603 wrote that rule for the Email Inbox card alone; this is it for every briefing — the story's
 * "3 Research packs", the reader box's "2 Possible ongoing matches", the selection's "694 Emails".
 * `briefing()` below runs every row (and its crumb text, `full`) through `capitalAfterCount`, so a
 * row is capitalised whichever module wrote it; `countLabel` is how a producer writes one in the
 * first place, with #603's thousands separator.
 *
 * Like #603 this DEVIATES from the frames, which write these rows in lower case ("3 research packs",
 * frame 40007883:371175; "124 emails · 9 require action", 40006958-296686) — on Saffet's request.
 * What stays lower case is the same count used MID-SENTENCE, which is not a row: the fallback
 * summary ("…come from 14 email groups"), the group subtitles ("91 emails · 75 unread"), the thread
 * list's loading line, chat prose and the rail/timeline cards (lib/entityTimeline.js). Only the
 * FIRST word after the number changes: "2 Possible ongoing matches", not "2 Possible Ongoing Matches".
 */
const LEADING_COUNT = /^(\d[\d,]*\s+)([a-z])/
export function capitalAfterCount(text) {
  return String(text ?? '').replace(LEADING_COUNT, (_, count, first) => `${count}${first.toUpperCase()}`)
}

/**
 * "2,322 Attachments" — a count and its noun as a Briefing row writes them (busy-my-web#609): the
 * count through `countText`, the noun singular at exactly one (`one`) and `many` otherwise
 * (default: `one` + "s"), its first letter capitalised.
 */
export function countLabel(n, one, many = `${one}s`) {
  const k = Number(n) || 0
  return capitalAfterCount(`${countText(k)} ${k === 1 ? one : many}`)
}

/**
 * Build a briefing, dropping any row with nothing to say.
 *
 * Empty rows are dropped rather than rendered blank because a briefing's whole job is to state
 * what IS there — "0 resources", "Type: unknown" and a bare bullet all read as a fault rather
 * than as information.
 */
export function briefing({ kind, title, titleIcon = 'global', titleColor = BRIEFING_PRIMARY, subtitle = '', note = '', titleAction = null, rows = [], packs = null, kindItems = null, selected = null, selectedLabel = '', loading = false, columns = 1 }) {
  const clean = (rows || [])
    // A PENDING row is the one row with nothing to say that is kept (busy-my-web#354): it is the
    // slot a count is on its way into, drawn as a shimmer of the row's own height so the card does
    // not reflow when the number lands. Everything else with no text is still dropped.
    .filter(r => r && (r.pending || String(r.text ?? '').trim()))
    // …and a pending row is only ever a placeholder: no text, no filter, nothing to press. Building
    // it here rather than letting the ordinary map run over it is what guarantees that — a row that
    // reached the strip with a `filter` on it would be clickable into a category nobody has counted.
    .map(r => (r.pending ? {
      key: r.key,
      icon: r.icon || 'global',
      text: '',
      pending: true,
      // How wide the shimmer bar is, in px — roughly the width of the words that will replace it
      // ("14,387 emails", "4 email groups"), so the swap is not a jump sideways either.
      pendingWidth: Number(r.pendingWidth) || 96,
      ...(Number(r.col) === 1 || Number(r.col) === 2 ? { col: Number(r.col) } : {}),
    } : {
      key: r.key,
      icon: r.icon || 'global',
      // Capital after the count, whoever wrote the row (busy-my-web#609 — see `capitalAfterCount`).
      text: capitalAfterCount(String(r.text).trim()),
      ...(r.filter ? { filter: r.filter, active: !!r.active } : {}),
      // `full` is the row's text WITH its count, kept beside the shortened one an active row wears
      // (busy-my-web#188). The breadcrumb names the same depth as a place — "2 tasks" — and a place
      // keeps its count; only the row above the list of those two tasks drops it.
      // …and the crumb's copy says the same capitalised words as the row (busy-my-web#609).
      ...(r.full && capitalAfterCount(r.full) !== capitalAfterCount(String(r.text).trim()) ? { full: capitalAfterCount(r.full) } : {}),
      // `action` is a row that DOES something rather than describing something — the story
      // briefing's "New sub-ongoing". A name, not a closure, so a briefing stays a plain value.
      ...(r.action ? { action: r.action } : {}),
      // A row that OPENS something of its own (the open thread's "Conversation" — Pouria, 2026-09-22).
      ...(r.open ? { open: String(r.open) } : {}),
      // `hint` is the row's hover text — the open thread's sender row names a person and keeps
      // their ADDRESS here (busy-my-web#611), so the fact is readable and the address is one hover away.
      ...(r.hint ? { hint: String(r.hint) } : {}),
      // `relations` is the email card's possible-Ongoing-matches row (busy-my-web#316): pressable
      // like a filter, but what it opens is the OFFERS underneath rather than a category of the
      // mailbox — so it carries its own flag and its own `active`, and the filter machinery (the
      // breadcrumb, `toggleFilter`, the row-collapsing in SmartBox) is left alone.
      ...(r.relations ? { relations: true, active: !!r.active } : {}),
      // …and WHICH thread's matches the row is about (busy-my-web#612): the email reader's row is
      // thread-scoped, the inbox card's is mailbox-wide, and the id is the only thing that tells
      // them apart downstream — a row rebuilt without it opened the mailbox view for both.
      ...(r.relations && r.relationsThread ? { relationsThread: String(r.relationsThread) } : {}),
      // `storyRelations` is the SAME row asked from inside an Ongoing (busy-my-web#333): the
      // story's own "N possible relations". It carries its own flag for the reason `relations`
      // does — pressable, but it opens the matches rather than narrowing anything, so
      // `storyFilters` and the breadcrumb are left alone — plus the `ongoingId` the unfold under
      // it needs to know WHOSE questions to draw. Both have to survive this map: a row that
      // reaches the strip without its flag renders as a plain <p> and can never be clicked.
      ...(r.storyRelations ? { storyRelations: true, ongoingId: String(r.ongoingId || ''), active: !!r.active } : {}),
      // …and its per-kind rows while the table is open (Pouria, 2026-10-01, frame
      // 40011046:161537) — the strip lists them under the crumb, each with its "View ↗".
      ...(r.storyRelations && Array.isArray(r.kinds) && r.kinds.length ? { kinds: r.kinds } : {}),
      // `span` is how many of a two-column card's columns the row takes. Only ever set to 1, and
      // only by a card whose plain FACTS are meant to pair up (the folder pages' inbox card,
      // busy-my-web#319): the rule everywhere else is that a row without a filter is a sentence
      // about all of them and runs the full width, and that rule is untouched.
      ...(Number(r.span) === 1 ? { span: 1 } : {}),
      // `col` is WHICH of a two-column card's columns the row is in — 1 or 2, stamped by
      // `emailBriefingGridOrder` (busy-my-web#338). It has to survive this map for the same reason
      // `relations` does: a row that reaches the strip without it is auto-placed, and an auto-placed
      // row after a column has run out lands across the aisle, which is the scrambled reading order
      // this issue is about.
      ...(Number(r.col) === 1 || Number(r.col) === 2 ? { col: Number(r.col) } : {}),
    }))
  // `subtitle` is the optional bold line between the title and the rows — the selection briefing's
  // "Selected", which says what the counts under it are ABOUT.
  // `titleAction` sits BESIDE the title rather than in the rows — the pack's Summary ⇄ Story
  // switch changes how the thing is shown, not which of its contents you are looking at.
  // `note` is a PARAGRAPH under the subtitle rather than a fact row — an explanation, not a count,
  // so it carries no glyph and nothing to press (the connect-email card's "Once connected, Busy
  // will brief you on …", frame 40011420:278006 / busy-my-web#451). ADDED only when there is one,
  // so every existing briefing stays byte-for-byte the value it was.
  const base = { kind, title, titleIcon, titleColor, subtitle: String(subtitle || ''), titleAction, rows: clean, loading: !!loading }
  const built = note ? { ...base, note: String(note) } : base
  // `packs` is the story briefing's expanded research-pack list (#56) — the packs themselves, under
  // the "N Research Packs" row, once that row is the filter in force. `kindItems` is the same idea
  // for every other kind (Pouria, 2026-08-15): the chosen kind's items, listed under its row.
  // Each is ADDED only when there is a list, so every other briefing is byte-for-byte the value
  // it was.
  const withPacks = packs ? { ...built, packs } : built
  const withItems = kindItems ? { ...withPacks, kindItems } : withPacks
  // `columns` is how many COLUMNS the rows are drawn in — the Email Briefing's dashboard is the one
  // the frame writes as two (busy-my-web#273). A layout hint rather than a second list: the rows and
  // their order are unchanged, so nothing else in the strip has to know. ADDED only when there is
  // more than one, for the same reason `packs` is: every other briefing stays the value it was.
  const withColumns = Number(columns) > 1 ? { ...withItems, columns: Number(columns) } : withItems
  // `selected` is what has been TICKED (frame 40007883:371467) — the Work-board's picks, named in
  // words under the rows, each openable from here. Its own key rather than a `subtitle` + rows,
  // because these entries carry an action ("Open") and the rows do not.
  //
  // `selectedLabel` is the heading over that list. It defaults to "Selected" (the frame's word for
  // a selection in hand) and is only ever set by a briefing where the same list means something
  // else — the comparison briefing calls them "Sources", because by then they are what the answer
  // was built from rather than what is waiting to be acted on (#77).
  return selected
    ? { ...withColumns, selected, ...(selectedLabel ? { selectedLabel: String(selectedLabel) } : {}) }
    : withColumns
}

/**
 * A row whose count is still on the wire (busy-my-web#354).
 *
 * The card opens in the click's own tick (#339) and its legs land at their own speeds — the mailbox
 * dashboard in one call, the sender groups in another, the attachments in a third. Before this, a
 * leg still in flight was simply not a row: the card drew what it had, and each late answer pushed
 * a new line into the grid several seconds later. That is the pop Saffet reported ("50 email groups
 * and 52 attachments come 5 seconds after").
 *
 * So a leg in flight IS a row — this one. It keeps the slot (the same key, and the same PLACE in
 * the reading order, so the column split — busy-my-web#530 — puts it exactly where the row it will
 * become goes), the same height, and roughly the same width, and the strip draws it as a shimmer. Nothing about it is pressable: it
 * states no count, so there is no category behind it to open.
 *
 * The row is a plain value like every other row here — the shimmer itself is the strip's business.
 */
export function pendingRow(key, icon, width = 96) {
  return { key: String(key), icon: icon || 'global', text: '', pending: true, pendingWidth: Number(width) || 96 }
}

/** A briefing with a title but nothing known yet — the strip shows it while a fetch is in flight. */
export function loadingBriefing({ kind, title, titleIcon, titleColor = BRIEFING_PRIMARY }) {
  // …in the colour the briefing's own header wears, so a card whose title is near-black
  // (the email inbox, busy-my-web#316) does not flash indigo for the length of its fetch.
  return briefing({ kind, title, titleIcon, titleColor, rows: [], loading: true })
}
